import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Input } from 'reactstrap';

import OrgTreeNodeTypeIcon from 'js/routes/Organizations/components/ManageOrg/OrgTreeVeiw/OrgTreeNodeTypeIcon';
import OrgWeightRowGutters from 'js/routes/Organizations/components/ManageOrg/OrgWeightRowGutters';
import {
  defaultWeightPlaceholderString,
  flattenOrgTreeForWeights,
  isEditableWeightRow,
  normalizeOrgForWeights,
  orgWeightResolutionKey,
  orgWeightRowKey,
  parentOrgIdForRow,
  overrideMapControlledInputValue,
  resolveOrgWeights,
} from './orgWeightsUtils';
import { useOrgWeights } from './OrgWeightsContext';
import styles from './OrgWeights.module.scss';

const EMPTY_WEIGHT_OVERRIDES = {};

function OrgWeightRowLabel({ row, pathToNode, children, readonly = false }) {
  return (
    <div
      className={cx(styles['weight-row'], {
        [styles['weight-row--readonly']]: readonly,
      })}
    >
      <div className={styles['weight-row-structure']}>
        <OrgWeightRowGutters pathToNode={pathToNode} />
        <div className={styles['weight-row-label']}>
          <OrgTreeNodeTypeIcon node={row} />
          <span>{row.name}</span>
        </div>
      </div>
      <div className={styles['weight-row-value']}>{children}</div>
    </div>
  );
}

/** Root org: label only — weight is not meaningful at this level. */
function OrgWeightRowRoot({ row, pathToNode }) {
  return <OrgWeightRowLabel row={row} pathToNode={pathToNode} />;
}

function OrgWeightRowReadOnlyValue({ row, pathToNode, value }) {
  const displayValue =
    value != null && Number.isFinite(value) ? String(value) : '';

  return (
    <OrgWeightRowLabel row={row} pathToNode={pathToNode} readonly>
      <Input
        className={cx(styles['weight-input'], styles['weight-input--readonly'])}
        readOnly
        tabIndex={-1}
        value={displayValue}
      />
    </OrgWeightRowLabel>
  );
}

function OrgWeightRowEditable({
  row,
  pathToNode,
  committedWeight,
  defaultWeight,
  disabled,
  onBlur,
}) {
  const [draftValue, setDraftValue] = useState(null);
  const cancelingRef = useRef(false);

  useEffect(() => {
    setDraftValue(null);
  }, [committedWeight]);

  const isEditing = draftValue !== null;
  const displayValue = isEditing
    ? draftValue
    : overrideMapControlledInputValue(committedWeight);

  const hasSpecifiedWeight = committedWeight !== undefined;

  const handleBlur = (ev) => {
    if (cancelingRef.current) {
      cancelingRef.current = false;
      setDraftValue(null);
      return;
    }
    onBlur(row, ev.target.value);
    setDraftValue(null);
  };

  const handleKeyDown = (ev) => {
    if (ev.key !== 'Escape') return;
    ev.preventDefault();
    cancelingRef.current = true;
    setDraftValue(null);
    ev.currentTarget.blur();
  };

  return (
    <OrgWeightRowLabel row={row} pathToNode={pathToNode}>
      <Input
        className={cx(styles['weight-input'], {
          [styles['weight-input--overridden']]: hasSpecifiedWeight,
        })}
        placeholder={defaultWeightPlaceholderString(defaultWeight)}
        value={displayValue}
        disabled={disabled}
        onChange={(ev) => setDraftValue(ev.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </OrgWeightRowLabel>
  );
}

function OrgCustomWeights({ organization, readOnly = false }) {
  const { config, saving, onWeightBlur } = useOrgWeights();

  const tree = useMemo(
    () => normalizeOrgForWeights(organization),
    [organization]
  );

  const rows = useMemo(
    () => (tree ? flattenOrgTreeForWeights(tree) : []),
    [tree]
  );

  const { defaultByKey, effectiveByKey } = useMemo(
    () =>
      resolveOrgWeights(
        tree,
        config.scheme,
        readOnly ? EMPTY_WEIGHT_OVERRIDES : config.weights,
        organization
      ),
    [tree, config.scheme, readOnly, config.weights, organization]
  );

  if (!tree || rows.length === 0) {
    return (
      <p className="text-muted mb-0">No organization structure to weight.</p>
    );
  }

  return (
    <div className={styles.weights}>
      <div className={styles.grid}>
        <div className={styles['grid-header']}>
          <div></div>
          <div className="text-right">Weight</div>
        </div>
        {rows.map((row) => {
          const resolutionKey = orgWeightResolutionKey(
            organization?.id,
            parentOrgIdForRow(row),
            row
          );
          const rowKey = orgWeightRowKey(
            organization?.id,
            parentOrgIdForRow(row),
            row
          );
          if (!isEditableWeightRow(row)) {
            return (
              <OrgWeightRowRoot
                key={resolutionKey || row.depthId}
                row={row}
                pathToNode={row.pathToNode}
              />
            );
          }

          if (readOnly) {
            return (
              <OrgWeightRowReadOnlyValue
                key={resolutionKey || row.depthId}
                row={row}
                pathToNode={row.pathToNode}
                value={effectiveByKey.get(resolutionKey)}
              />
            );
          }

          return (
            <OrgWeightRowEditable
              key={rowKey || row.depthId}
              row={row}
              pathToNode={row.pathToNode}
              committedWeight={config.weights[rowKey]}
              defaultWeight={defaultByKey.get(rowKey)}
              disabled={saving}
              onBlur={onWeightBlur}
            />
          );
        })}
      </div>
    </div>
  );
}

export default OrgCustomWeights;
